import { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError, formatAdminDate } from './adminUserDisplay.js';
import { ADMIN_PROSPECT_STATUSES } from '../../services/admin/prospectsListRequest.js';

export { ADMIN_DATE_PLACEHOLDER, classifyAdminCallableError, formatAdminDate };
export { ADMIN_PROSPECT_STATUSES };

const STATUS_BADGE_CLASS = Object.freeze({
  new: 'bg-slate-100 text-slate-800 ring-slate-200',
  contacted: 'bg-sky-100 text-sky-900 ring-sky-200',
  follow_up: 'bg-amber-100 text-amber-950 ring-amber-200',
  owner_responded: 'bg-violet-100 text-violet-900 ring-violet-200',
  qualified: 'bg-emerald-100 text-emerald-900 ring-emerald-200',
  not_interested: 'bg-rose-100 text-rose-900 ring-rose-200',
  converted: 'bg-indigo-100 text-indigo-900 ring-indigo-200',
});

/**
 * @param {string | null | undefined} status
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function adminProspectStatusLabel(status, t) {
  if (!status) return ADMIN_DATE_PLACEHOLDER;
  const key = `admin.prospection.status.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

/**
 * Visible-text status badge classes. Color is secondary to the label.
 * @param {string | null | undefined} status
 */
export function adminProspectStatusBadgeClass(status) {
  return STATUS_BADGE_CLASS[status] || 'bg-slate-100 text-slate-800 ring-slate-200';
}

/**
 * Street line only — city/postal live in their own columns/cards.
 * @param {{ address?: string | null } | null | undefined} item
 */
export function formatAdminProspectAddress(item) {
  const address = typeof item?.address === 'string' ? item.address.trim() : '';
  return address;
}

/**
 * City + postal for list/card secondary line.
 * @param {{ city?: string | null, postalCode?: string | null, province?: string | null } | null | undefined} item
 */
export function formatAdminProspectLocality(item) {
  const parts = [item?.city, item?.postalCode]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.join(' ');
}

/**
 * @param {unknown} value
 */
export function formatAdminProspectUnits(value) {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const n = Number(value);
  if (!Number.isFinite(n)) return ADMIN_DATE_PLACEHOLDER;
  return String(n);
}

/**
 * @param {string | null | undefined} kind
 */
export function adminProspectListErrorKey(kind) {
  if (kind === 'unauthenticated') return 'admin.prospection.error.unauthenticated';
  if (kind === 'permission-denied') return 'admin.prospection.error.permission';
  if (kind === 'invalid-argument') return 'admin.prospection.error.invalid';
  if (kind === 'not-found') return 'admin.prospection.error.notFound';
  if (kind === 'failed-precondition') return 'admin.prospection.error.failedPrecondition';
  if (kind === 'unavailable') return 'admin.prospection.error.unavailable';
  if (kind === 'internal') return 'admin.prospection.error.internal';
  return 'admin.prospection.error';
}

/**
 * Create-form errors. failed-precondition is the duplicate addressKey contract.
 * @param {string | null | undefined} kind
 */
export function adminProspectCreateErrorKey(kind) {
  if (kind === 'failed-precondition') return 'admin.prospection.error.duplicate';
  return adminProspectListErrorKey(kind);
}

/**
 * Analysis create/link dialog errors. failed-precondition covers stale version,
 * conflicting link, and incomplete address.
 * @param {string | null | undefined} kind
 */
export function adminProspectAnalysisConversionErrorKey(kind) {
  if (kind === 'failed-precondition') return 'admin.prospection.analysisConversion.error.conflict';
  if (kind === 'invalid-argument') return 'admin.prospection.analysisConversion.error.invalid';
  if (kind === 'not-found') return 'admin.prospection.analysisConversion.error.notFound';
  return adminProspectListErrorKey(kind);
}

export function adminProspectNoteErrorKey(kind) {
  if (kind === 'invalid-argument') return 'admin.prospection.note.error.invalid';
  return adminProspectListErrorKey(kind);
}

export function adminProspectSourceLabel(source, t) {
  if (!source) return ADMIN_DATE_PLACEHOLDER;
  const key = `admin.prospection.source.${source}`;
  const label = t(key);
  return label === key ? source : label;
}

export function adminProspectActivityTypeLabel(type, t) {
  if (type === 'note') return t('admin.prospection.activity.note');
  if (type === 'status_change') return t('admin.prospection.activity.statusChange');
  if (type === 'public_response') return t('admin.prospection.activity.publicResponse');
  return t('admin.prospection.activity.unknown');
}

export function isoToDatetimeLocal(value) {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function datetimeLocalToIso(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return null;
  return date.toISOString();
}

export function isFollowUpOverdue(value, now = Date.now()) {
  if (value == null || value === '') return false;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return false;
  return date.getTime() < now;
}

function ownerText(value) {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Primary owner is the editable projection, never owners[1] after a compact.
 * @param {{ ownerName?: string | null, ownerMailingAddress?: string | null } | null | undefined} detail
 */
export function adminProspectPrimaryOwner(detail) {
  const src = detail && typeof detail === 'object' ? detail : {};
  return {
    name: ownerText(src.ownerName),
    mailingAddress: ownerText(src.ownerMailingAddress),
  };
}

function ownerHasIdentity(owner) {
  if (!owner || typeof owner !== 'object' || Array.isArray(owner)) return false;
  return Boolean(ownerText(owner.name) || ownerText(owner.mailingAddress));
}

/**
 * Additional owners are slots 1..n. Slot 0 is never promoted.
 * @param {{ owners?: unknown[] | null } | null | undefined} detail
 */
export function adminProspectAdditionalOwners(detail) {
  const owners = Array.isArray(detail?.owners) ? detail.owners : [];
  return owners.slice(1).filter(ownerHasIdentity);
}

export const LIST_PROJECTION_EXCLUDED_FIELDS = Object.freeze([
  'addressKey',
  'adminSearchAddress',
  'adminSearchCity',
  'adminSearchPostalCode',
  'adminTargetingKeys',
  'adminTargetingVersion',
  'ownerMailingAddress',
  'notes',
  'createdByUid',
  'updatedByUid',
  'primaryContact',
  'latestOwnerProvidedPropertyProfile',
  'latestOwnerProvidedFinancialSnapshot',
  'publicResponseSummary',
  'latestImportedPropertyProfile',
  'latestMunicipalAssessment',
  'latestRegistryActivity',
  'lotNumbers',
  'cadastralDivision',
  'municipalRollNumber',
  'lotMatchKeys',
]);

export const ADMIN_PROSPECT_UNIT_MIX_KEYS = Object.freeze([
  'oneAndHalf',
  'twoAndHalf',
  'threeAndHalf',
  'fourAndHalf',
  'fiveAndHalf',
  'sixAndHalfPlus',
  'commercial',
  'other',
]);

export const ADMIN_PROSPECT_RESPONSE_INTENTS = Object.freeze([
  'property_value',
  'property_sale',
  'profitability_guide',
  'legacy_owner_response',
]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function translatedOrNull(key, t) {
  const label = t(key);
  return label === key ? null : label;
}

export function adminProspectCampaignDetailPath(campaignId) {
  if (typeof campaignId !== 'string') return null;
  const id = campaignId.trim();
  if (!id || id.length > 128 || /[\\/]/.test(id)) return null;
  return `/admin/prospection/campaigns/${id}`;
}

export function displayAdminProspectEmail(value) {
  if (typeof value !== 'string') return null;
  const email = value.trim().toLowerCase();
  if (!email || !EMAIL_PATTERN.test(email)) return null;
  return email;
}

export function displayAdminProspectPhone(value) {
  if (typeof value !== 'string') return null;
  const phone = value.trim();
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return phone;
}

export function adminProspectTelHref(phone) {
  const visible = displayAdminProspectPhone(phone);
  if (!visible) return null;
  const trimmed = visible.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;
  return `tel:${hasPlus ? `+${digits}` : digits}`;
}

export function adminProspectResponseIntentLabel(intent, t) {
  if (!intent) return null;
  return translatedOrNull(`admin.prospection.activity.intent.${intent}`, t);
}

export function adminProspectUnitMixLabel(key, t) {
  return translatedOrNull(`admin.prospection.ownerResponse.unit.${key}`, t);
}

export function adminProspectVerificationLabel(state, t) {
  if (state === 'admin_verified') return t('admin.prospection.ownerResponse.verification.admin_verified');
  return t('admin.prospection.ownerResponse.verification.unverified');
}

export function adminProspectProjectionStatusLabel(status, t) {
  if (!status) return t('admin.prospection.ownerResponse.projectionStatus.unknown');
  const label = translatedOrNull(`admin.prospection.ownerResponse.projectionStatus.${status}`, t);
  return label || t('admin.prospection.ownerResponse.projectionStatus.unknown');
}

export function adminProspectSourceTypeLabel(sourceType, t) {
  if (sourceType === 'public_qr_response') {
    return t('admin.prospection.ownerResponse.source.public_qr_response');
  }
  return null;
}

export function adminProspectPeriodLabel(period, t) {
  if (period === 'annual_current') return t('admin.prospection.ownerResponse.period.annual_current');
  return null;
}

export function formatAdminProspectCents(cents, language) {
  if (cents === null) return { kind: 'unknown' };
  if (!Number.isInteger(cents) || cents < 0) return { kind: 'invalid' };
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  return {
    kind: 'currency',
    text: new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100),
  };
}

export function formatAdminProspectCentsLabel(cents, language, t) {
  const formatted = formatAdminProspectCents(cents, language);
  if (formatted.kind === 'unknown') return t('admin.prospection.ownerResponse.unknown');
  if (formatted.kind === 'currency') return formatted.text;
  return null;
}

export function formatAdminProspectLandArea(landArea, language, t) {
  if (!landArea || typeof landArea !== 'object') {
    return t('admin.prospection.importedProperty.unavailable');
  }
  const value = typeof landArea.value === 'number' && Number.isFinite(landArea.value) ? landArea.value : null;
  const unit = landArea.unit === 'm2' || landArea.unit === 'ft2' ? landArea.unit : null;
  if (value == null || !unit) return t('admin.prospection.importedProperty.unavailable');
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const formattedValue = new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value);
  const unitLabel = t(`admin.prospection.importedProperty.landArea.unit.${unit}`);
  return `${formattedValue} ${unitLabel}`;
}

export function adminRegistryActivityTypeLabel(type, t) {
  if (!type) return null;
  const label = translatedOrNull(`admin.prospection.importedProperty.registryActivity.type.${type}`, t);
  return label || null;
}

export function adminImportProvenanceSourceLabel(sourceType, t) {
  if (!sourceType) return null;
  return translatedOrNull(`admin.prospection.importedProperty.source.${sourceType}`, t);
}

export function formatAdminProspectUnavailable(t) {
  return t('admin.prospection.importedProperty.unavailable');
}

export function expectedSourceFromProvenance(provenance) {
  if (!provenance || typeof provenance !== 'object') return null;
  const campaignId = typeof provenance.campaignId === 'string' ? provenance.campaignId.trim() : '';
  const mailingId = typeof provenance.mailingId === 'string' ? provenance.mailingId.trim() : '';
  const responseIntent = provenance.responseIntent;
  const submittedAt = typeof provenance.submittedAt === 'string' ? provenance.submittedAt : '';
  if (!campaignId || !mailingId || !submittedAt) return null;
  if (!ADMIN_PROSPECT_RESPONSE_INTENTS.includes(responseIntent)) return null;
  return { campaignId, mailingId, responseIntent, submittedAt };
}

export function adminProspectVerificationErrorKey(kind) {
  if (kind === 'failed-precondition') return 'admin.prospection.ownerResponse.verification.stale';
  return adminProspectListErrorKey(kind);
}

export function shouldApplyAdminProspectOwnedResult({
  mounted,
  requestId,
  activeRequestId,
  ownerProspectId,
  activeProspectId,
}) {
  if (!mounted || !requestId || requestId !== activeRequestId) return false;
  if (ownerProspectId !== undefined || activeProspectId !== undefined) {
    return Boolean(ownerProspectId && ownerProspectId === activeProspectId);
  }
  return true;
}
